import { useEffect, useState } from 'react';
import { Alert, Box, Button, Group, Loader, Text } from '@mantine/core';
import { IconAlertCircle, IconDownload } from '@tabler/icons-react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { fetchDocumentPreviewObjectUrl, downloadDocument } from '../api/uploadedFileApi';

type PreviewFileLike = {
    Id?: number;
    id?: number;
    FileName?: string;
    fileName?: string;
    Url?: string;
    url?: string;
    SourceUrl?: string;
    sourceUrl?: string;
};

interface Props {
    file: PreviewFileLike | null;
    initialPage?: number;
}

const getSafeNameAndUrl = (file: PreviewFileLike | null) => {
    const name = file?.FileName ?? file?.fileName ?? '';
    const url = file?.Url ?? file?.url ?? file?.SourceUrl ?? file?.sourceUrl ?? '';
    return { name, url };
};

const getFileExtension = (name?: string, urlIfNeeded?: string): string => {
    const pick = (value?: string) => {
        if (!value || typeof value !== 'string') return '';
        const dot = value.lastIndexOf('.');
        return dot >= 0 ? value.slice(dot + 1).toLowerCase() : '';
    };

    let ext = pick(name);
    if (ext) return ext;

    if (urlIfNeeded) {
        try {
            const parsedUrl = new URL(urlIfNeeded);
            const last = parsedUrl.pathname.split('/').pop() ?? '';
            ext = pick(last);
            if (ext) return ext;
        } catch { }

        ext = pick(urlIfNeeded);
    }

    return ext || '';
};

export default function DocumentPreviewPane({ file, initialPage }: Props) {
    const [pdfKey, setPdfKey] = useState(0);
    // Object URL for the document fetched through our authenticated endpoint.
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    // Start in the loading state so the first render shows the spinner rather
    // than briefly flashing the "Preview unavailable" download/retry fallback
    // (objectUrl is still null before the fetch effect runs).
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    const { name, url } = getSafeNameAndUrl(file);
    const fileId = file?.Id ?? file?.id ?? null;
    const ext = getFileExtension(name, url);
    const defaultLayout = defaultLayoutPlugin();

    // Load a PDF rendition of the document through our own endpoint rather than
    // its storage URL, so the storage location is never exposed, locally-stored
    // files work, and office documents (docx/xls/...) render inline too - the
    // server converts them. The object URL is revoked on cleanup.
    useEffect(() => {
        if (!file || (fileId === null && !url)) {
            setObjectUrl(null);
            return;
        }

        let isActive = true;
        let createdUrl: string | null = null;

        setIsLoading(true);
        setLoadError(null);

        fetchDocumentPreviewObjectUrl({ fileId, url })
            .then((resolvedUrl) => {
                if (!isActive) {
                    if (resolvedUrl) URL.revokeObjectURL(resolvedUrl);
                    return;
                }
                if (!resolvedUrl) {
                    setLoadError('Unable to load this document.');
                    setObjectUrl(null);
                    return;
                }
                createdUrl = resolvedUrl;
                setObjectUrl(resolvedUrl);
                setPdfKey((current) => current + 1);
            })
            .finally(() => {
                if (isActive) setIsLoading(false);
            });

        return () => {
            isActive = false;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [file, fileId, url, reloadToken]);

    const handleRetry = () => setReloadToken((current) => current + 1);

    if (!file || (fileId === null && !url)) {
        return (
            <Box p="md">
                <Alert icon={<IconAlertCircle size="1rem" />} title="No file" color="yellow" variant="light">
                    File reference is missing, so preview is unavailable.
                </Alert>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                <Group>
                    <Loader size="md" />
                    <Text size="sm" c="dimmed">Loading document...</Text>
                </Group>
            </Box>
        );
    }

    // No PDF rendition could be produced (e.g. an unsupported format, or
    // LibreOffice unavailable on the host). Fall back to an authenticated
    // download of the original file.
    if (loadError || !objectUrl) {
        return (
            <Box p="md" style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                <Alert
                    icon={<IconAlertCircle size="1rem" />}
                    title="Preview unavailable"
                    color="blue"
                    variant="light"
                    style={{ maxWidth: 460 }}
                >
                    <Text size="sm" mb="md">
                        {loadError || `${name || 'This document'} could not be rendered for preview.`}
                        {ext ? ` Download the ${ext.toUpperCase()} file to open it locally.` : ''}
                    </Text>
                    <Group>
                        <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconDownload size={14} />}
                            onClick={() => downloadDocument({ fileId, url }, name)}
                        >
                            Download
                        </Button>
                        <Button size="xs" variant="subtle" onClick={handleRetry}>
                            Retry
                        </Button>
                    </Group>
                </Alert>
            </Box>
        );
    }

    return (
            <Box style={{ height: '100%', background: '#f0f0f0', position: 'relative' }}>
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                    <Viewer
                        key={pdfKey}
                        fileUrl={objectUrl}
                        plugins={[defaultLayout]}
                        initialPage={initialPage}
                        defaultScale={1.0}
                        renderLoader={(progress) => (
                            <Box style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                <Group>
                                    <Loader size="md" />
                                    <Text size="sm" c="dimmed">
                                        {typeof progress === 'number' ? `Loading PDF... ${Math.round(progress)}%` : 'Loading PDF...'}
                                    </Text>
                                </Group>
                            </Box>
                        )}
                        renderError={(error) => (
                            <Alert color="red" icon={<IconAlertCircle size="1rem" />} title="PDF viewer error">
                                <Text size="sm" mb="xs">
                                    {error?.message || error?.name || 'Failed to load PDF.'}
                                </Text>
                                <Group>
                                    <Button size="xs" variant="light" onClick={handleRetry}>
                                        Retry
                                    </Button>
                                    <Button
                                        size="xs"
                                        variant="light"
                                        leftSection={<IconDownload size={14} />}
                                        onClick={() => downloadDocument({ fileId, url }, name)}
                                    >
                                        Download
                                    </Button>
                                </Group>
                            </Alert>
                        )}
                    />
                </Worker>
            </Box>
    );
}
